import { ColumnDef } from '@tanstack/react-table'
import { values } from 'lodash'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable } from '../../../Components/TanstackTable'
import useReportTable from '../useReportTable'
import { ActivityData, AgentActivityReportProps } from './types'
import { getActivityOveralls } from './utils'

export const AgentActivityReportTable = (props: AgentActivityReportProps) => {
    const { reportsData, isLoading } = props
    const { t } = useTranslation()

    const reports = useMemo(() => values(reportsData) || [], [reportsData])
    const activityTypes = useMemo(() => reports[0]?.activities || [], [reports])

    const columns = useMemo<ColumnDef<ActivityData>[]>(() => {
        const agentColumn: ColumnDef<ActivityData> = {
            id: 'agent',
            header: t('app.common.agent'),
            enableSorting: false,
            accessorFn: (row) => `${row.agent?.firstName} ${row.agent?.lastName}`,
        }

        const activityColumns: ColumnDef<ActivityData>[] = activityTypes?.map(({ id, name }) => ({
            id: `${id}`,
            header: name,
            enableSorting: false,
            cell: (info) => info.row.original.activities?.find((activity) => activity?.id === id)?.count,
        }))

        const totalColumn: ColumnDef<ActivityData> = {
            id: 'total_by_agent',
            header: t('app.common.total'),
            enableSorting: false,
            cell: (info) => <div className="fw-bolder text-center">{info.row.original.agentGrandTotal}</div>,
        }

        return [agentColumn, ...activityColumns, totalColumn]
    }, [activityTypes, t])

    const tableFooter = useMemo(() => {
        const { activityOveralls, grandOverall } = getActivityOveralls(reports, activityTypes)

        return (
            <tr>
                <td>{t('app.common.overall')}</td>
                {activityOveralls.map((activity) => (
                    <td key={activity.id}>{activity.count}</td>
                ))}
                <td className="fw-bolder text-center">{grandOverall}</td>
            </tr>
        )
    }, [reports, activityTypes, t])

    const table = useReportTable(columns, reports)

    return (
        <DataTable
            table={table}
            tableFooter={tableFooter}
            isLoading={isLoading}
            theadClass="dmn-table-header"
            autoLayout
        />
    )
}
